import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Info, MapPin, Phone, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, fileUrl, formatDate, formatPrice } from "@/lib/api";
import { Page } from "@/components/layout/Layout";
import { Loading, ErrorBox } from "@/components/common/States";
import { ConditionBadge, StateBadge, VerifiedTick, Stars } from "@/components/common/Badges";
import { CompareToggle } from "@/components/common/Cards";
import SpecTable from "@/components/common/SpecTable";
import ReportButton from "@/components/common/ReportButton";
import ProductReviews from "@/components/reviews/ProductReviews";
import SEO from "@/components/common/SEO";

const FUNC = { ok: "Sorunsuz", issue: "Sorunlu", broken: "Arızalı" };

function Gallery({ urls, title }) {
  const [i, setI] = useState(0);
  if (!urls.length) return <div className="panel aspect-[4/3] grid place-items-center text-zinc-500">Fotoğraf yok</div>;
  return (
    <div className="space-y-2">
      <div className="panel aspect-[4/3] overflow-hidden"><img src={fileUrl(urls[i])} alt={title} className="h-full w-full object-contain bg-[#f5f5f7]" data-testid="gallery-main" /></div>
      <div className="flex gap-2 overflow-x-auto">
        {urls.map((u, n) => (
          <button key={u} onClick={() => setI(n)} data-testid={`gallery-thumb-${n}`} className={`h-16 w-16 shrink-0 overflow-hidden rounded border ${n === i ? "border-[#0071e3]" : "border-black/[0.08]"}`}>
            <img src={fileUrl(u)} alt={title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

function ContactBox({ l }) {
  const [phone, setPhone] = useState(null);
  const [err, setErr] = useState("");
  const reveal = async () => {
    try { setPhone((await api.get(`/listings/${l.id}/phone`)).data.phone); } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <div className="panel space-y-3 p-5" data-testid="contact-box">
      <Link to={`/satici/${l.seller.id}`} className="flex items-center gap-2 font-medium hover:text-[#0077ed]" data-testid="seller-link">
        {l.seller.avatar_url ? <img src={fileUrl(l.seller.avatar_url)} alt={l.seller.full_name || "Satıcı"} loading="lazy" decoding="async" className="h-9 w-9 rounded-full object-cover" /> : <span className="grid h-9 w-9 place-items-center rounded-full bg-black/[0.05]">{l.seller.full_name?.[0]}</span>}
        {l.seller.full_name} <VerifiedTick show={l.seller.is_verified_seller} />
      </Link>
      {phone ? (
        <a href={`tel:${phone.replace(/\s/g, "")}`} data-testid="seller-phone" className="flex items-center gap-2 rounded border border-[#0071e3] px-3 py-2 font-mono text-lg text-[#0077ed]"><Phone className="h-4 w-4" />{phone}</a>
      ) : (
        <Button onClick={reveal} data-testid="reveal-phone-button" className="w-full bg-[#0071e3] hover:bg-[#0077ed] text-white"><Phone className="h-4 w-4" />Telefon numarasını göster</Button>
      )}
      {err && <ErrorBox message={err} />}
      <p className="flex gap-2 text-xs text-zinc-500"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />CreateFPV ödeme veya teslimat almaz. Alışveriş site dışında, alıcı ile satıcı arasında gerçekleşir. Ürünü görmeden ödeme yapmayın.</p>
    </div>
  );
}

function ConditionBox({ l }) {
  const c = l.condition;
  if (!c) return null;
  return (
    <div className="panel p-5 space-y-2" data-testid="condition-box">
      <div className="flex items-center gap-2"><p className="font-display font-semibold">Kondisyon</p><ConditionBadge code={l.condition_class} testId="detail-condition-badge" /></div>
      {l.condition_label && <p className="text-sm text-zinc-700">{l.condition_label}</p>}
      <dl className="grid grid-cols-2 gap-2 text-sm">
        {c.usage_days != null && <><dt className="text-zinc-500">Kullanım süresi</dt><dd>{c.usage_days} gün</dd></>}
        {c.usage_months != null && <><dt className="text-zinc-500">Kullanım süresi</dt><dd>{c.usage_months} ay</dd></>}
        {c.crash_count != null && <><dt className="text-zinc-500">Kaza sayısı</dt><dd>{c.crash_count}</dd></>}
        <dt className="text-zinc-500">Çalışma durumu</dt><dd>{FUNC[c.functional_status]}</dd>
      </dl>
      {c.notes && <p className="text-sm text-zinc-500">{c.notes}</p>}
      <p className="text-xs text-yellow-700">Kondisyon bilgisi satıcı beyanıdır; CreateFPV tarafından doğrulanmamıştır.</p>
    </div>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const { data: l, isLoading, error } = useQuery({ queryKey: ["listing", id], queryFn: async () => (await api.get(`/listings/${id}`)).data });
  if (isLoading) return <Loading />;
  if (error) return <Page><ErrorBox message={errMsg(error)} /></Page>;
  return (
    <Page>
      <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr]">
        <Gallery urls={l.photo_urls} title={l.title} />
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <StateBadge state={l.item_state} /><ConditionBadge code={l.condition_class} />
            <span className="text-xs text-zinc-500">{l.category_info.name}</span>
            {l.status !== "active" && <span className="rounded bg-red-500/20 px-2 text-xs text-red-700" data-testid="listing-inactive-badge">Yayında değil</span>}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold leading-tight" data-testid="listing-title">{l.title}</h1>
          <p className="font-display text-3xl font-semibold text-zinc-900" data-testid="listing-price">{formatPrice(l.price, l.currency)}</p>
          <div className="flex flex-wrap gap-4 text-sm text-zinc-500">
            <span className="flex items-center gap-1" data-testid="listing-location"><MapPin className="h-4 w-4" />{l.district}, {l.province}</span>
            <span>İlan tarihi: {formatDate(l.published_at || l.created_at)}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <CompareToggle type="listing" id={l.id} title={l.title} category={l.category} />
            {l.is_owner && <Button asChild size="sm" variant="outline" className="bg-transparent" data-testid="edit-listing-button"><Link to={`/ilan/${l.id}/duzenle`}><Pencil className="h-3.5 w-3.5" />Düzenle</Link></Button>}
            <ReportButton targetType="listing" targetId={l.id} label="İlanı bildir" />
          </div>
          <ContactBox l={l} />
        </div>
      </div>
      <div className="mt-10 grid gap-8 lg:grid-cols-[1.3fr_1fr]">
        <div className="space-y-8">
          <section><h2 className="font-display text-xl font-semibold">Açıklama</h2><p className="mt-3 whitespace-pre-line text-zinc-700" data-testid="listing-description">{l.description}</p></section>
          <section>
            <h2 className="font-display text-xl font-semibold mb-3">Teknik özellikler</h2>
            <SpecTable fields={l.category_info.fields} specs={l.specs} catalogSpecs={l.catalog_specs} overriddenKeys={l.product ? Object.keys(l.spec_overrides || {}) : []} />
            {!l.product && <p className="mt-2 text-xs text-yellow-700">Bu ürün katalogda yok; teknik veriler satıcı beyanıdır.</p>}
          </section>
          {l.product && <ProductReviews productId={l.product.id} productTitle={l.product.title} />}
        </div>
        <div className="space-y-4">
          <ConditionBox l={l} />
          {l.product && (
            <Link to={`/katalog/${l.product.id}`} className="panel lift block p-5" data-testid="catalog-model-link">
              <p className="eyebrow">Katalog modeli</p>
              <p className="mt-1 font-display font-semibold">{l.product.title}</p>
              <div className="mt-1 flex items-center gap-2 text-xs text-zinc-500"><Stars value={l.product.rating.average || 0} size={3} />{l.product.rating.count} ürün değerlendirmesi</div>
            </Link>
          )}
        </div>
      </div>
    </Page>
  );
}
